import { useEffect, useMemo, useState } from "react";
import styles from "./list.module.css";
import dayjs from "dayjs";
import axios from "axios";
import download from "downloadjs";
import { debounce } from "lodash";
import { Alert, Button, Chip, Dialog, Divider, FormControl, IconButton, InputAdornment, InputLabel, ListSubheader, MenuItem, Paper, Select, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, useMediaQuery } from "@mui/material";
import { Calendar, Search } from "react-feather";
import { DateRangePicker } from "rsuite";
import Loader from "../../../components/Loader";
import { StyledMainTableCell, StyledMainTableRow } from "../../../components/TableComponent";
import TablePaginationComponent from "../../../components/TablePagination";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import BookingDetailsPopup from "./bookingDetailsPopup";
import DeliveryBookingDetailsPopup from "./deliveryBookingDetailsPopup";
import BookingCard from "./BookingCard";
import StatusChip from "./StatusChip";
import ActionListItem from "./ActionListItem";
import PageHeader from "../../../components/PageHeader";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import BookingListLoader from "./BookingListLoader";
import BookingPaymentPopup from "../../../components/popups/BookingPaymentPopup";
import DeletePopup from "../../../components/DeletePopup";
import SendInvoicePopup from "../../../components/popups/SendInvoicePopup";
import { ClearRounded } from "@mui/icons-material";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import { companyCountries } from "../../../../utils/companyCountries";
import { BOOKING_STATUS, bookingStatusGroups, LOCAL_DELIVERY_STATUS, LOCAL_DELIVERY_STATUS_GROUPS, isLocalDelivery } from "../../../../utils/bookingStatus";

// service: "international" (Bookings menu) or "local_delivery"
const BookingList = ({ service = "international" }) => {
  const localOnly = service === "local_delivery";
  const STATUS = localOnly ? LOCAL_DELIVERY_STATUS : BOOKING_STATUS;
  const STATUS_GROUPS = localOnly ? LOCAL_DELIVERY_STATUS_GROUPS : bookingStatusGroups(companyCountries());
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const isMobile = useMediaQuery("(max-width:768px)");
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [bookedCount, setBookedCount] = useState(0);
  const [transitCount, setTransitCount] = useState(0);
  const [deliveredCount, setDeliveredCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filterVars, setFilterVars] = useState({ searchKey: "", status: "", date: null });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelId, setCancelId] = useState(0);
  const [cancelPaymentId, setCancelPaymentId] = useState(0);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewDeliveryModalOpen, setViewDeliveryModalOpen] = useState(false);
  const [viewId, setViewId] = useState(0);
  const [viewStr, setViewStr] = useState("");
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectBooking, setSelectBooking] = useState(null);
  const [sendInvoiceModalOpen, setSendInvoiceModalOpen] = useState(false);
  const [invoiceMailId, setInvoiceMailId] = useState(0);
  const [invoiceMail, setInvoiceMail] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const { websiteData } = useWebsite();
  useSiteBranding(localOnly ? "Delivery bookings" : "Booking list", websiteData);

  const debouncedSetSearch = useMemo(() =>
    debounce((value) => {
      setFilterVars((prev) => ({ ...prev, searchKey: value }));
    }, 500),
    []
  );

  useEffect(() => {
    return () => debouncedSetSearch.cancel();
  }, [debouncedSetSearch]);

  const paginationChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginationChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchBookings();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (page === 0) {
      fetchBookings();
    } else {
      setPage(0);
    }
  }, [filterVars]);

  const fetchBookings = () => {
    if (isFirstLoad) {
      setContentLoading(true);
    }

    axios.get(process.env.REACT_APP_API_URL + "v1/bookings", {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      },
      params: {
        service,
        page: page + 1,
        per_page: rowsPerPage,
        search: filterVars.searchKey,
        status: filterVars.status,
        start_date: filterVars.date ? dayjs(filterVars.date[0]).format("YYYY-MM-DD") : null,
        end_date: filterVars.date ? dayjs(filterVars.date[1]).format("YYYY-MM-DD") : null,
      },
    }).then((response) => {
      setFilteredData(response.data.data.data);
      setTotalCount(response.data.data.status_count.total || 0);
      setBookedCount(response.data.data.status_count.booked || 0);
      setTransitCount(response.data.data.status_count.transit || 0);
      setDeliveredCount(response.data.data.status_count.delivered || 0);

      if (isFirstLoad) {
        setContentLoading(false);
        setIsFirstLoad(false);
      }
    }).catch(() => {
      if (isFirstLoad) {
        setContentLoading(false);
        setIsFirstLoad(false);
      }
    });
  }

  const cancelBooking = () => {
    setLoading(true);
    setCancelModalOpen(false);

    axios.put(process.env.REACT_APP_API_URL + "v1/bookings/" + cancelId, { status: -1 }, {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        setSnackbarOpen(true);
        setSnackbarType("success");
        setSnackbarMessage("Booking cancelled successfully.");

        setFilteredData((prev) =>
          prev.map((item) => item.id === cancelId ? { ...item, status: -1 } : item));
        setCancelId(0);
      }
    }).catch(() => {
      setLoading(false);
    });
  }

  const printBooking = (id) => {
    setLoading(true);

    axios(process.env.REACT_APP_API_URL + "v1/bookings-item-pdf/" + id, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then((response) => {
      setLoading(false);
      download(response.data, "booking-" + id + ".pdf", response.headers["content-type"]);
    })
      .catch(() => {
        setLoading(false);
      });
  }

  const cancelPayment = () => {
    setLoading(true);

    axios.put(process.env.REACT_APP_API_URL + "v1/bookings/" + cancelPaymentId, { note: "", payment_status: 0 }, {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then((response) => {
      setLoading(false);

      if (response.data.status === "SUCCESS") {
        setSnackbarOpen(true);
        setSnackbarType("success");
        setSnackbarMessage(response.data.message);

        setFilteredData((prev) =>
          prev.map((item) => item.id === cancelPaymentId ? { ...item, payment_status: 0 } : item));
        setCancelPaymentId(0);
      }
    }).catch(() => {
      setLoading(false);
    });
  }

  const paymentItem = (response) => {
    setLoading(false);
    setPaymentModalOpen(false);
    setSnackbarOpen(true);
    setSnackbarType("success");
    setSnackbarMessage(response.message);

    setFilteredData((prev) =>
      prev.map((item) => {
        if (item.id === response.data.id) {
          return { ...item, payment_status: response.data.payment_status, paid_amount: response.data.paid_amount, due_balance: response.data.due_balance };
        }
        return item;
      })
    );

    setSelectBooking(null);
  }

  const sendMailBooking = () => {
    setSendInvoiceModalOpen(false);
    setLoading(true);

    axios(process.env.REACT_APP_API_URL + "v1/send-booking-pdf/" + invoiceMailId, {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then((response) => {
      setLoading(false);

      if (response.data.status === "SUCCESS") {
        setSnackbarOpen(true);
        setSnackbarType("success");
        setSnackbarMessage(response.data.message);
      }
    }).catch(() => {
      setLoading(false);
    });
  }

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />

    <div className={styles.MainDiv}>
      <PageHeader title={localOnly ? "Shop Orders" : "Bookings"} trail={[{ label: localOnly ? "Shop Orders" : "Bookings" }, { label: "List" }]} />

      <div className={styles.MainCard}>

        {contentLoading && <BookingListLoader />}

        {!contentLoading && (<>

          <div className={styles.SummaryGrid}>
            <div className={styles.SummaryCard}>
              <h2>{totalCount}</h2>
              <p>Total Orders</p>
            </div>

            <div className={styles.SummaryCard}>
              <h2>{bookedCount}</h2>
              <p>Booked</p>
            </div>

            <div className={styles.SummaryCard}>
              <h2>{transitCount}</h2>
              <p>{localOnly ? "Out For Delivery" : "In Transit"}</p>
            </div>

            <div className={styles.SummaryCard}>
              <h2>{deliveredCount}</h2>
              <p>Delivered</p>
            </div>
          </div>

          <div className={styles.FilterBar}>
            <div className={styles.SearchBox}>
              <Search className={styles.SearchIcon} />
              <TextField
                fullWidth
                placeholder="Search booking ID, customer, postcode..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  debouncedSetSearch(e.target.value);
                }}
                InputProps={{
                  endAdornment: searchInput && (<InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => {
                        setSearchInput("");
                        debouncedSetSearch("");
                      }}
                    >
                      <ClearRounded fontSize="small" />
                    </IconButton>
                  </InputAdornment>),
                }}
              />
            </div>

            <div className={styles.FilterItem}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterVars.status}
                  label="Status"
                  onChange={(e) => {
                    setFilterVars((prev) => ({ ...prev, status: e.target.value }));
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  {STATUS_GROUPS.flatMap((group) => [
                    group.label ? <ListSubheader key={`h-${group.key}`} className={styles.StatusGroupHeader}>{group.label}</ListSubheader> : <Divider key={`h-${group.key}`} />,
                    ...group.statuses.map((status) => (<MenuItem key={status} value={status}>
                      <span className={styles.TagColor} style={{ background: STATUS[status].color, marginRight: 8 }} />
                      {STATUS[status].label}
                    </MenuItem>)),
                  ])}
                </Select>
              </FormControl>
            </div>

            <div className={styles.DateFilter}>
              <DateRangePicker
                size="lg"
                format="dd/MM/yyyy"
                placement="bottomEnd"
                showOneCalendar
                value={filterVars.date}
                caretAs={() => <Calendar size={15} />}
                placeholder="Select Date"
                onChange={(value) => {
                  setFilterVars((prev) => ({ ...prev, date: value }));
                }}
              />
            </div>
          </div>

          <div className={styles.LegendScroll}>
            <div className={styles.LegendGroups}>
              {STATUS_GROUPS.map((group) => (
                <div className={styles.LegendGroup} key={group.key}>
                  {group.label && <span className={styles.LegendGroupLabel}>{group.label}</span>}
                  <div className={styles.CoutryTagSec}>
                    {group.statuses.map((status) => (
                      <div className={styles.CoutryTag} key={status}>
                        <span className={styles.TagColor} style={{ background: STATUS[status].color }} />
                        {STATUS[status].label}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {!isMobile ? (<div className={styles.TableCard}>
            <TableContainer component={Paper}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <StyledMainTableCell>Booking ID</StyledMainTableCell>
                    <StyledMainTableCell>Customer</StyledMainTableCell>
                    <StyledMainTableCell>Order Date</StyledMainTableCell>
                    <StyledMainTableCell>Total</StyledMainTableCell>
                    <StyledMainTableCell>Paid</StyledMainTableCell>
                    <StyledMainTableCell>Due</StyledMainTableCell>
                    <StyledMainTableCell>Payment</StyledMainTableCell>
                    <StyledMainTableCell>Status</StyledMainTableCell>
                    <StyledMainTableCell>{localOnly ? "Delivery Postcode" : "Pickup Postcode"}</StyledMainTableCell>
                    <StyledMainTableCell>{localOnly ? "Delivery Date" : "Pickup Date"}</StyledMainTableCell>
                    <StyledMainTableCell>Created By</StyledMainTableCell>
                    <StyledMainTableCell align="center">Actions</StyledMainTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredData.length === 0 && (<TableRow>
                    <TableCell colSpan={12} align="center" style={{ padding: 60 }}>No bookings found.</TableCell>
                  </TableRow>)}
                  {filteredData.map((item) => {
                    return (<StyledMainTableRow hover key={item.id} className={`${styles.BookingRow} ${styles["Status" + item.status]}`}>
                      <TableCell>
                        <strong>#{item.booking_id}</strong>
                        <span
                          className={item.freight_type === 'Air Freight' ? styles.FreightTagAir : styles.FreightTagSea}
                          title={isLocalDelivery(item) ? 'Shop Order' : item.freight_type === 'Air Freight' ? 'Air Freight' : 'Sea Freight'}
                        >
                          {isLocalDelivery(item) ? <LocalShippingOutlined /> : item.freight_type === 'Air Freight' ? <FlightTakeoffIcon /> : <DirectionsBoatIcon />}
                        </span>
                      </TableCell>
                      <TableCell>{item.customer_name}</TableCell>
                      <TableCell>{dayjs(item.created_at).format("DD MMM YYYY")}</TableCell>
                      <TableCell>£{Number(item.total).toFixed(2)}</TableCell>
                      <TableCell>£{Number(item.paid_amount).toFixed(2)}</TableCell>
                      <TableCell>£{Number(item.due_balance).toFixed(2)}</TableCell>
                      <TableCell>{item.status === -1 ? <Chip label="Cancelled" color="error" size="small" /> : parseInt(item.payment_status) === 1 ? <Chip label="Paid" color="success" size="small" /> : <Button size="small" className={styles.StatusBU} onClick={() => { setSelectBooking(item); setPaymentModalOpen(true); }}>Unpaid</Button>}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={item.status} localDelivery={isLocalDelivery(item)} />
                      </TableCell>
                      <TableCell>{item.post_code}</TableCell>
                      <TableCell>{dayjs(item.pickup_date).format("DD MMM YYYY")}</TableCell>
                      <TableCell>{item.created_user}</TableCell>
                      <TableCell align="center">
                        <ActionListItem
                          item={item}
                          printBooking={printBooking}
                          setViewModalOpen={setViewModalOpen}
                          setViewDeliveryModalOpen={setViewDeliveryModalOpen}
                          setViewId={setViewId}
                          setViewStr={setViewStr}
                          setCancelModalOpen={setCancelModalOpen}
                          setCancelId={setCancelId}
                          setInvoiceMail={setInvoiceMail}
                          setInvoiceMailId={setInvoiceMailId}
                          setSendInvoiceModalOpen={setSendInvoiceModalOpen}
                        />
                      </TableCell>

                    </StyledMainTableRow>);
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>) : (<div className={styles.MobileCards}>
            {filteredData.map((item) => (<BookingCard
              key={item.id}
              item={item}
              printBooking={printBooking}
              setViewModalOpen={setViewModalOpen}
              setViewDeliveryModalOpen={setViewDeliveryModalOpen}
              setViewId={setViewId}
              setViewStr={setViewStr}
              setCancelModalOpen={setCancelModalOpen}
              setCancelId={setCancelId}
              setInvoiceMail={setInvoiceMail}
              setInvoiceMailId={setInvoiceMailId}
              setSendInvoiceModalOpen={setSendInvoiceModalOpen}
            />))}
          </div>)}

          <div className={styles.PaginationWrap}>
            <TablePaginationComponent
              count={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              handleChangePage={paginationChangePage}
              handleChangeRowsPerPage={paginationChangeRowsPerPage}
            />
          </div>
        </>)}
      </div>

      <Dialog fullWidth maxWidth="md" open={viewModalOpen} onClose={(e) => setViewModalOpen(false)} PaperProps={{ className: styles.DialogPaper }}>
        <BookingDetailsPopup bookingId={viewId} viewStr={viewStr} setViewModalOpen={setViewModalOpen.bind(this)} setLoading={setLoading.bind(this)} />
      </Dialog>

      <Dialog fullWidth maxWidth="md" open={viewDeliveryModalOpen} onClose={(e) => setViewDeliveryModalOpen(false)} PaperProps={{ className: styles.DialogPaper }}>
        <DeliveryBookingDetailsPopup bookingId={viewId} viewStr={viewStr} setViewDeliveryModalOpen={setViewDeliveryModalOpen.bind(this)} setLoading={setLoading.bind(this)} />
      </Dialog>

      <Dialog open={paymentModalOpen} onClose={(e) => setPaymentModalOpen(false)} fullWidth maxWidth="sm" PaperProps={{ className: styles.DialogPaper }}>
        <BookingPaymentPopup modalClose={setPaymentModalOpen.bind(this)} paymentItem={paymentItem.bind(this)} selectBooking={selectBooking} setLoading={setLoading.bind(this)} />
      </Dialog>

      <Dialog open={sendInvoiceModalOpen} onClose={() => setSendInvoiceModalOpen(false)} fullWidth maxWidth="sm" PaperProps={{ className: styles.DialogPaper }}>
        <SendInvoicePopup sendMailBooking={sendMailBooking.bind(this)} setSendInvoiceModalOpen={setSendInvoiceModalOpen.bind(this)} setInvoiceMail={setInvoiceMail.bind(this)} invoiceMail={invoiceMail} />
      </Dialog>

      <DeletePopup open={cancelModalOpen} setOpen={setCancelModalOpen.bind(this)} confirmAction={cancelBooking.bind(this)} title="Cancel Booking?" message="Are you sure you want to cancel this booking? You can't undo this action." btnText="Yes, Cancel" cancelText="No" />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbarType}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  </>);

};

export default withAdminAuth(BookingList);